import { useEffect, useRef } from 'react'
import { type Types, RenderingEngine } from '@cornerstonejs/core'
import * as cornerstoneTools from '@cornerstonejs/tools'
import { createDrawingOverlay, createDrawingRect, updateDrawingRect } from '../../../utils/svgUtils'
import { convertCanvasToImageCoords } from '../../../utils/coordinateTransforms'
import { DRAWING_CONFIG } from '../../../constants/phi'
import type { PHIPrediction, InstanceMetadata, PHIContainer } from '../../../types/phi'

const { ToolGroupManager, StackScrollTool } = cornerstoneTools

// Define types for timepoint submission data
type TimePointSubmissionData = {
  data?: {
    timepoint_submission: {
      container_submissions: Array<{
        container_submission_id: string
        exam_metadata?: Array<{
          exam_metadata_id: string
          series?: Array<{
            series_metadata_id: string
          }>
        }>
      }>
    }
  }
}

interface UseDrawingModeProps {
  isDrawingMode: boolean
  elementRef: React.RefObject<HTMLDivElement>
  engine: RenderingEngine | null
  currentImageId: string | null
  urlToMetadataMap: Record<string, InstanceMetadata>
  selectedSeries: {
    series: string
    study: string
    exam_metadata_id: string
    series_metadata_id: string
  }
  phiData: { containers?: PHIContainer[] } | undefined
  timePointSubmissionData: TimePointSubmissionData | undefined
  onAddPHIPrediction: (sopInstanceUid: string, prediction: PHIPrediction) => void
  onSavePhiChanges: (prediction: PHIPrediction, containerSubmissionId: string) => Promise<void>
  onForceRedraw: () => void
}

export function useDrawingMode({
  isDrawingMode,
  elementRef,
  engine,
  currentImageId,
  urlToMetadataMap,
  selectedSeries,
  phiData,
  timePointSubmissionData,
  onAddPHIPrediction,
  onSavePhiChanges,
  onForceRedraw,
}: UseDrawingModeProps) {
  const drawingStateRef = useRef({
    isDrawing: false,
    startX: 0,
    startY: 0,
    drawingRect: null as SVGRectElement | null,
    drawingOverlay: null as SVGElement | null,
  })

  // Ref updated on every render so event handlers always see the current props
  // without re-registering listeners (re-registering with all deps causes
  // infinite re-renders because callbacks like onAddPHIPrediction change identity).
  // currentImageId is the most critical: it determines which SOP instance the
  // bounding box is saved to, so it MUST be the slice the user is actually on.
  const latestPropsRef = useRef({
    currentImageId,
    urlToMetadataMap,
    selectedSeries,
    engine,
    phiData,
    timePointSubmissionData,
    onAddPHIPrediction,
    onSavePhiChanges,
    onForceRedraw,
  })
  latestPropsRef.current = {
    currentImageId,
    urlToMetadataMap,
    selectedSeries,
    engine,
    phiData,
    timePointSubmissionData,
    onAddPHIPrediction,
    onSavePhiChanges,
    onForceRedraw,
  }

  // Handle drawing mode changes
  useEffect(() => {
    console.log('Drawing mode effect:', { isDrawingMode })

    const element = elementRef.current
    if (!element || !isDrawingMode) {
      console.log('Drawing mode: early return', { hasElement: !!element, isDrawingMode })
      return
    }

    console.log('Setting up drawing mode')

    const drawingState = drawingStateRef.current

    // Change cursor to crosshair in drawing mode
    element.style.cursor = 'crosshair'

    // Disable stack scroll tool during drawing
    const toolGroup = ToolGroupManager.getToolGroup('STACK_TOOL_GROUP_ID')
    if (toolGroup) {
      toolGroup.setToolPassive(StackScrollTool.toolName)
    }

    // Handle start of drawing
    const handleDrawingStart = (e: MouseEvent) => {
      console.log('Drawing start event')
      if (!isDrawingMode) return

      // Get mouse position relative to viewport
      const rect = element.getBoundingClientRect()
      drawingState.startX = e.clientX - rect.left
      drawingState.startY = e.clientY - rect.top
      console.log('Drawing start position:', { x: drawingState.startX, y: drawingState.startY })

      // Create SVG overlay if it doesn't exist
      if (!drawingState.drawingOverlay) {
        drawingState.drawingOverlay = createDrawingOverlay(element)
        console.log('Created drawing overlay')
      }

      // Create a new rectangle element
      drawingState.drawingRect = createDrawingRect(drawingState.startX, drawingState.startY)
      drawingState.drawingOverlay.appendChild(drawingState.drawingRect)
      drawingState.isDrawing = true
      console.log('Started drawing rectangle')
    }

    // Handle drawing in progress
    const handleDrawingMove = (e: MouseEvent) => {
      if (!isDrawingMode || !drawingState.isDrawing || !drawingState.drawingRect) return

      // Get current mouse position
      const rect = element.getBoundingClientRect()
      const currentX = e.clientX - rect.left
      const currentY = e.clientY - rect.top

      // Update rectangle dimensions
      updateDrawingRect(drawingState.drawingRect, drawingState.startX, drawingState.startY, currentX, currentY)
    }

    // Handle completion of drawing
    const handleDrawingComplete = async () => {
      console.log('Drawing complete event')
      if (!isDrawingMode || !drawingState.isDrawing || !drawingState.drawingRect || !drawingState.drawingOverlay) {
        console.log('Drawing complete: early return')
        return
      }

      drawingState.isDrawing = false

      // Get final dimensions
      const x = parseFloat(drawingState.drawingRect.getAttribute('x') || '0')
      const y = parseFloat(drawingState.drawingRect.getAttribute('y') || '0')
      const width = parseFloat(drawingState.drawingRect.getAttribute('width') || '0')
      const height = parseFloat(drawingState.drawingRect.getAttribute('height') || '0')
      console.log('Final drawing dimensions:', { x, y, width, height })

      // Ignore tiny boxes (likely accidental clicks)
      if (width < DRAWING_CONFIG.minBoxSize || height < DRAWING_CONFIG.minBoxSize) {
        drawingState.drawingRect.remove()
        console.log('Drawing too small, removed')
        return
      }

      // Read fresh props from the ref — critical so currentImageId reflects the
      // slice the user is actually on, not the slice from effect registration time.
      const {
        currentImageId: latestImageId,
        engine: latestEngine,
        urlToMetadataMap: latestUrlMap,
        selectedSeries: latestSeries,
        phiData: latestPhiData,
        timePointSubmissionData: latestTpData,
        onAddPHIPrediction: addPrediction,
        onSavePhiChanges: savePhi,
        onForceRedraw: forceRedraw,
      } = latestPropsRef.current

      // Convert canvas coordinates to image coordinates
      if (latestImageId && latestEngine) {
        console.log('Converting coordinates and saving PHI box')
        const viewport = latestEngine.getViewport('STACK_VIEWPORT') as Types.IStackViewport
        if (!viewport) {
          console.error('No viewport found')
          return
        }

        try {
          // Get current image information
          const imageUrl = latestImageId.slice('wadouri:'.length) || ''
          const metadata = latestUrlMap[imageUrl]
          const sopInstanceUid = metadata?.SOPInstanceUID

          if (!sopInstanceUid) {
            console.error('Could not find SOP Instance UID for image:', imageUrl)
            return
          }

          console.log('Converting coordinates for SOP:', sopInstanceUid)

          // Convert coordinates
          const imageCoords = convertCanvasToImageCoords(x, y, width, height, viewport, latestImageId)
          console.log('Image coordinates:', imageCoords)

          // Create a new PHI prediction object
          const newPrediction: PHIPrediction = {
            study_instance_uid: latestSeries.study,
            series_instance_uid: latestSeries.series,
            sop_instance_uid: sopInstanceUid,
            x: imageCoords.x,
            y: imageCoords.y,
            width: imageCoords.width,
            height: imageCoords.height,
            action: 'apply',
            scope: 'single',
            source: 'USER',
            exam_metadata_id: '',
            series_metadata_id: '',
          }

          console.log('Created new prediction:', newPrediction)

          // Get the container info for the current series
          if (latestPhiData?.containers && Array.isArray(latestPhiData.containers)) {
            // Find the container that matches the current series
            const matchingContainer = latestPhiData.containers.find((container) => {
              const matchingExam = container.exams.find((exam) => exam.study_instance_uid === latestSeries.study)

              if (matchingExam) {
                const matchingSeries = matchingExam.series.find(
                  (series) => series.series_instance_uid === latestSeries.series,
                )

                if (matchingSeries) {
                  newPrediction.exam_metadata_id = matchingExam.exam_metadata_id
                  newPrediction.series_metadata_id = matchingSeries.series_metadata_id
                  return true
                }
              }

              return false
            })

            if (matchingContainer) {
              // Save the prediction
              await savePhi(newPrediction, matchingContainer.container_submission_id)
              addPrediction(sopInstanceUid, newPrediction)
            } else {
              // Fallback to first container submission
              const containerSubmissions = latestTpData?.data?.timepoint_submission.container_submissions || []

              if (containerSubmissions.length > 0) {
                const fallbackContainerSubmission = containerSubmissions[0]
                const examMetadata = fallbackContainerSubmission.exam_metadata?.[0]
                const seriesMetadata = examMetadata?.series?.[0]

                if (examMetadata?.exam_metadata_id && seriesMetadata?.series_metadata_id) {
                  newPrediction.exam_metadata_id = examMetadata.exam_metadata_id
                  newPrediction.series_metadata_id = seriesMetadata.series_metadata_id

                  await savePhi(newPrediction, fallbackContainerSubmission.container_submission_id)
                  addPrediction(sopInstanceUid, newPrediction)
                }
              } else {
                console.error('No container submissions found for this timepoint')
              }
            }
          }

          // Force redraw to show the new PHI box
          forceRedraw()
        } catch (error) {
          console.error('Error creating new PHI box:', error)
        }
      }

      // Clean up the drawing overlay
      drawingState.drawingOverlay.remove()
      drawingState.drawingOverlay = null
      drawingState.drawingRect = null
    }

    // Add event listeners for drawing
    element.addEventListener('mousedown', handleDrawingStart)
    element.addEventListener('mousemove', handleDrawingMove)
    element.addEventListener('mouseup', handleDrawingComplete)

    // Clean up event listeners when drawing mode changes or component unmounts
    return () => {
      element.removeEventListener('mousedown', handleDrawingStart)
      element.removeEventListener('mousemove', handleDrawingMove)
      element.removeEventListener('mouseup', handleDrawingComplete)

      // Reset cursor
      element.style.cursor = 'default'

      // Re-enable stack scroll tool — restore both Primary and Wheel bindings
      const toolGroup = ToolGroupManager.getToolGroup('STACK_TOOL_GROUP_ID')
      if (toolGroup) {
        toolGroup.setToolActive(StackScrollTool.toolName, {
          bindings: [
            { mouseButton: cornerstoneTools.Enums.MouseBindings.Primary },
            { mouseButton: cornerstoneTools.Enums.MouseBindings.Wheel },
          ],
        })
      }

      // Remove any drawing overlays
      const existingOverlay = element.querySelector('.drawing-overlay') as SVGElement
      if (existingOverlay) {
        existingOverlay.remove()
      }

      // Reset drawing state
      drawingState.isDrawing = false
      drawingState.drawingRect = null
      drawingState.drawingOverlay = null
    }
  }, [
    isDrawingMode,
    elementRef,
    // Remove these dependencies temporarily to stop infinite re-renders
    // engine,
    // currentImageId,
    // urlToMetadataMap,
    // selectedSeries,
    // phiData,
    // timePointSubmissionData,
    // onAddPHIPrediction,
    // onSavePhiChanges,
    // onForceRedraw,
  ])

  return {
    // No return values needed for this hook
  }
}
